import { doc, getDoc, getDocs, collection, query, where, limit, Firestore } from 'firebase/firestore'
import type { QueryDocumentSnapshot, DocumentSnapshot } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import type { Tenant, TenantConfig } from '~/types/tenant'
import type { Locales } from '~/types/types'

export function buildTenantConfigFromEnv(): TenantConfig {
  const config = useRuntimeConfig().public
  return {
    slug: 'default',
    identityPlatformTenantId: '',
    appName: (config.appName as string) || 'PictureClub',
    instanceLogo: (config.instanceLogo as string) || '',
    animationText: (config.animationText as string) || '',
    logoOnly: String(config.logoOnly) === 'true',
    hideRollFilter: String(config.hideRollFilter) === 'true',
    frontpageHeader: (config.frontpageHeader as string) || '',
    fontFamily: (config.fontFamily as string) || '',
    accentColor: (config.accentColor as string) || '',
    showProviderBranding: String(config.showProviderBranding) !== 'false',
    requireRegistration: String(config.requireRegistration) === 'true',
    allowAnonymousLogin: String(config.allowAnonymousLogin) === 'true',
    allowPublicAccess: String(config.allowPublicAccess) === 'true',
    cameraRollSortOrder: (config.cameraRollSortOrder as string) === 'asc' ? 'asc' : 'desc',
    registrationCode: (config.registrationCode as string) || '',
    featureQrSharing: String(config.featureQrSharing) !== 'false',
    featurePagetransitions: Boolean(config.featurePagetransitions),
    defaultLocale: 'de' as Locales,
    licenseBadge: (config.licenseBadge as string) || '',
    licenseUrl: (config.licenseUrl as string) || '',
    licenseLabel: (config.licenseLabel as string) || '',
    licenseCredit: (config.licenseCredit as string) || '',
  }
}

export const useTenant = () => {
  return useState<TenantConfig>('tenant-config', buildTenantConfigFromEnv)
}

export function isMultiTenant(): boolean {
  return useRuntimeConfig().public.tenantMode === 'multi'
}

/**
 * Derives the tenant slug from a `*.pictureclub.io` hostname (the first label).
 * Returns the explicit override if set, and null for hostnames that are not a
 * pictureclub.io subdomain — those are custom domains (e.g. fotos.sjmuz.ch) and
 * must be resolved by the tenant doc's `domain` field instead (see
 * resolveTenantDoc).
 */
export function extractTenantSlugFromHostname(hostname: string): string | null {
  const override = useRuntimeConfig().public.tenantOverride as string
  if (override) return override
  if (!hostname.endsWith('.pictureclub.io')) return null
  const parts = hostname.split('.')
  if (parts.length < 2) return null
  return parts[0]
}

/**
 * Resolves the tenant Firestore doc for a hostname. Two paths:
 *   - pictureclub.io subdomains → direct getDoc(tenants/{slug}) (cheap).
 *   - custom domains (any other host) → query tenants where domain == hostname.
 * Returns the doc snapshot (whose id is the slug) or null if none matches.
 */
async function resolveTenantDoc(
  db: Firestore,
  hostname: string,
): Promise<DocumentSnapshot | QueryDocumentSnapshot | null> {
  const slug = extractTenantSlugFromHostname(hostname)
  if (slug) {
    const snap = await getDoc(doc(db, 'tenants', slug))
    return snap.exists() ? snap : null
  }
  // Custom domain: look up by the tenant doc's `domain` field.
  const q = query(collection(db, 'tenants'), where('domain', '==', hostname), limit(1))
  const res = await getDocs(q)
  return res.empty ? null : res.docs[0]
}

// In-flight (memoized) tenant load. Shared by the tenant plugin and the auth
// middleware so that whichever runs first triggers the Firestore fetch and both
// await the same promise — the middleware never reads tenant flags (e.g.
// allowPublicAccess) before the doc has resolved.
let tenantLoadPromise: Promise<void> | null = null

export function ensureTenantLoaded(): Promise<void> {
  // Single-tenant mode is fully env-driven (useState default), nothing to fetch.
  // Guard against any server pass (prerender) — resolution is client-only.
  if (!isMultiTenant() || typeof window === 'undefined') return Promise.resolve()
  if (!tenantLoadPromise) tenantLoadPromise = loadTenantFromFirestore()
  return tenantLoadPromise
}

async function loadTenantFromFirestore(): Promise<void> {
  const tenantState = useTenant()
  const hostname = window.location.hostname
  const db = useNuxtApp().$db as Firestore
  try {
    const snap = await resolveTenantDoc(db, hostname)
    if (!snap) {
      console.error(`[tenant] No tenant config found for hostname "${hostname}"`)
      return
    }
    const slug = snap.id
    const data = snap.data() as Tenant
    if (!data.identityPlatformTenantId) {
      console.error(`[tenant] Tenant "${slug}" is missing identityPlatformTenantId — sign-in will fail. Re-run create-tenant with --identityPlatformTenantId.`)
      return
    }
    tenantState.value = {
      slug,
      identityPlatformTenantId: data.identityPlatformTenantId,
      appName: data.appName,
      instanceLogo: data.instanceLogo || '',
      animationText: data.animationText || '',
      logoOnly: data.logoOnly ?? false,
      hideRollFilter: data.hideRollFilter ?? false,
      frontpageHeader: data.frontpageHeader || '',
      fontFamily: data.fontFamily || '',
      accentColor: data.accentColor || '',
      showProviderBranding: data.showProviderBranding ?? false,
      requireRegistration: data.requireRegistration ?? false,
      allowAnonymousLogin: data.allowAnonymousLogin ?? false,
      allowPublicAccess: data.allowPublicAccess ?? false,
      cameraRollSortOrder: data.cameraRollSortOrder || 'desc',
      registrationCode: data.registrationCode || '',
      featureQrSharing: data.featureQrSharing ?? true,
      featurePagetransitions: data.featurePagetransitions ?? false,
      defaultLocale: data.defaultLocale || 'de',
      licenseBadge: data.licenseBadge || '',
      licenseUrl: data.licenseUrl || '',
      licenseLabel: data.licenseLabel || '',
      licenseCredit: data.licenseCredit || '',
    } satisfies TenantConfig

    // Scope Firebase Auth to the resolved IdP tenant. Every subsequent sign-in
    // (anonymous, password, social, email-link) on this Auth instance will be
    // issued tokens with firebase.tenant == identityPlatformTenantId, which
    // Firestore rules require.
    getAuth().tenantId = data.identityPlatformTenantId

    console.debug(`[tenant] Resolved slug "${slug}" → IdP tenant "${data.identityPlatformTenantId}"`)
  } catch (e) {
    console.error('[tenant] Failed to load tenant doc', e)
  }
}
